<script setup>
import { 
	ref,
	computed,
	onMounted, 
	onUpdated, 
	nextTick,
} from 'vue'
import { UUID } from '@/modules/util/uuid'
import { useInspectionStore } from '@/stores/inspection-store'

import splatEstate from '@/assets/splats/splat-estate.png'
import splatHatchback from '@/assets/splats/splat-hatchback.png'
import splatHGVTractor from '@/assets/splats/splat-hgv-tractor.png'
import splatHGV from '@/assets/splats/splat-hgv.png'
import splatLCV from '@/assets/splats/splat-lcv.png'
import splatPickup from '@/assets/splats/splat-pickup.png'
import splatTrailer from '@/assets/splats/splat-trailer.png'
import splat from '@/assets/splats/splat.png'

const splatMap = {
	'splat-estate': splatEstate,
	'splat-hatchback': splatHatchback,
	'splat-hgv-tractor': splatHGVTractor,
	'splat-hgv': splatHGV,
	'splat-lcv': splatLCV,
	'splat-pickup': splatPickup,
	'splat-trailer': splatTrailer,
	'splat': splat,
}

defineExpose({
	splats: Object.keys(splatMap)
})

const store = useInspectionStore()
const props = defineProps(['modelValue', 'height', 'width', 'readonly', 'splatType', 'showAll'])
const emit = defineEmits(['update:modelValue'])
const container = ref(null)
const markId = new UUID().get()
const activeSplat = computed(() => splatMap[store.currentNamaSplat || props.splatType || 'splat'])

const addDamageItem = (item, index) => {
	if (!item || !item.damagemappos || item.damagemappos.x === undefined || item.damagemappos.y === undefined) {
		return
	}
	updateMarker(item.damagemappos.x, item.damagemappos.y, `damage-pos-marker-${index}`, 'primary')
}

const updateDamageMapPos = async (event) => {
	if (props.readonly) {
		return
	}
	
	await nextTick()
    
	const { offsetX, offsetY, target } = event
	const x = Math.round((offsetX / target.width) * 100)
	const y = Math.round((offsetY / target.height) * 100)
    
	emit('update:modelValue', { x, y, splat: props.splatType || 'splat' })

	updateMarker(x, y, markId)
}

const updateMarker = (x, y, id=null, theme='success') => {
	if (!(x && y)) {
		return
	}

	if (!id) {
		id = `damage-pos-marker-${x}-${y}`
	}

	let marker = document.getElementById(id)

	if (!marker) {
		marker = document.createElement('div')
		marker.id = id
		marker.className = `position-absolute rounded-circle pa-2 bg-${theme}`
		marker.style.boxShadow = '0 0 8px 0 rgba(0, 80, 0, 0.5)'
		marker.style.transform = 'translate(-50%, -50%)'
	}

	marker.style.left = `${x}%`
	marker.style.top = `${y}%`

	container.value.appendChild(marker)
}

onMounted(() => {
	if (props.showAll) {
		const items = store.currentDamageItems || []
	
		items.forEach((item, i) => {
			addDamageItem(item, i)
		})
	}

	updateMarker(props.modelValue.x, props.modelValue.y, markId)

	if (props.height) {
		container.value.style.height = props.height
	}
	if (props.width) {
		container.value.style.width = props.width
	}
})

onUpdated(() => {
	if (props.showAll) {
		const items = store.currentDamageItems || []
	
		items.forEach((item, i) => {
			addDamageItem(item, i)
		})
	}

	updateMarker(props.modelValue.x, props.modelValue.y, markId)
})
</script>

<template>
<div
	ref="container"
	class="position-relative"
>
	<img
		:src="activeSplat"
		alt="Splash Damage Plot"
		class="w-100 h-100"
		@click="updateDamageMapPos"
	>
</div>
</template>

<style lang="scss" scoped>

</style>